import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { User as UserIcon, Save, Bell, ShieldCheck, Loader2 } from "lucide-react";

// ---------------------------------------------------------------------------
// SPRINT 1 · TASK 3 — Supervising Administrator self-service profile.
//
// A signed-in admin may edit their OWN display name, business title, phone,
// profile image, notification preferences, and email signature.
// Email change requires re-authentication (current password).
// Role / permissions / division assignments CANNOT be changed here — the
// UI does not render controls for them, and the backend PATCH refuses
// any attempt via a strict allow-list.
// ---------------------------------------------------------------------------

const CANONICAL_NOTIFICATION_PREFS = [
  { key: "daily_digest",        label: "Daily digest email (submissions, holds, and reviews)" },
  { key: "urgent_compliance",   label: "Urgent compliance alerts (holds, breach detections)" },
  { key: "assignment_updates",  label: "Assignment updates (new records routed to me)" },
  { key: "weekly_summary",      label: "Weekly submission summary" },
];

export default function MyProfilePage() {
  const { user, refresh: refreshAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState("");                // required only if changing email
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/profile");
        setProfile(r.data);
        setNewEmail(r.data.email || "");
      } catch (e) {
        toast.error(errMsg(e, "Could not load profile."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function updateField(key, value) {
    setProfile((p) => ({ ...(p || {}), [key]: value }));
  }
  function updatePref(key, value) {
    setProfile((p) => ({ ...(p || {}), notification_prefs: { ...(p?.notification_prefs || {}), [key]: value } }));
  }

  async function saveProfile() {
    if (!profile) return;
    setSaving(true);
    try {
      const body = {
        display_name: profile.display_name || "",
        business_title: profile.business_title || "",
        phone: profile.phone || "",
        profile_image_url: profile.profile_image_url || "",
        notification_prefs: profile.notification_prefs || {},
        email_signature: profile.email_signature || "",
      };
      const r = await api.patch("/me/profile", body);
      setProfile(r.data);
      toast.success("Profile updated.");
      refreshAuth?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile."));
    } finally {
      setSaving(false);
    }
  }

  async function saveEmail() {
    if (!newEmail || newEmail === profile.email) return;
    if (!pwd) {
      toast.error("Enter your current password to change your email.");
      return;
    }
    setChangingEmail(true);
    try {
      const r = await api.patch("/me/profile", {
        new_email: newEmail,
        current_password: pwd,
      });
      toast.success("Email updated. For security you will be signed out.");
      setProfile(r.data);
      // The backend has rotated the ``prt`` token counter, invalidating
      // the existing session. Force a fresh sign-in.
      setTimeout(() => { window.location.assign("/login"); }, 1200);
    } catch (e) {
      toast.error(errMsg(e, "Could not update email."));
    } finally {
      setChangingEmail(false);
      setPwd("");
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <PageHeader
        title="My profile"
        description="Update your own display information, contact details and notification preferences. Role and permission settings are managed by Super Admin."
      />

      {/* Read-only identity */}
      <Card data-testid="my-profile-identity-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserIcon className="w-4 h-4 opacity-70" /> Identity
          </CardTitle>
          <CardDescription>
            These fields identify your account. Role, permissions and account activation
            are managed by a Super Admin and cannot be changed here.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-[13px]">
          <ReadOnly label="Signed-in name" value={profile.name} testid="profile-ro-name" />
          <ReadOnly label="Role" value={profile.role} testid="profile-ro-role" />
          <ReadOnly label="Account status" value={profile.is_active ? "Active" : "Inactive"} testid="profile-ro-status" />
          <ReadOnly label="Member since" value={profile.created_at?.slice(0, 10)} testid="profile-ro-created" />
        </CardContent>
      </Card>

      {/* Editable profile */}
      <Card data-testid="my-profile-editable-card">
        <CardHeader>
          <CardTitle className="text-base">Display &amp; contact</CardTitle>
          <CardDescription>How you appear on Kandake correspondence and internal listings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Display name" value={profile.display_name} onChange={(v) => updateField("display_name", v)}
                   placeholder="e.g. Adamma Nkechi" testid="profile-display-name" />
            <Field label="Business title" value={profile.business_title} onChange={(v) => updateField("business_title", v)}
                   placeholder="e.g. Head of Compliance" testid="profile-business-title" />
            <Field label="Telephone" value={profile.phone} onChange={(v) => updateField("phone", v)}
                   placeholder="+27 11 555 0000" testid="profile-phone" />
            <Field label="Profile image URL" value={profile.profile_image_url}
                   onChange={(v) => updateField("profile_image_url", v)}
                   placeholder="https://…"
                   testid="profile-image-url" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Email signature
            </Label>
            <Textarea
              value={profile.email_signature || ""}
              onChange={(e) => updateField("email_signature", e.target.value)}
              placeholder={"— Full name\nHead of Compliance, Kandake\n+27 …"}
              rows={4}
              className="mt-1 text-[12.5px]"
              data-testid="profile-email-signature"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Rendered at the end of outbound emails you send from Kandake.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={saving} data-testid="profile-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification preferences */}
      <Card data-testid="my-profile-notifications-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-4 h-4 opacity-70" /> Notification preferences
          </CardTitle>
          <CardDescription>Enable or disable the emails Kandake sends you.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {CANONICAL_NOTIFICATION_PREFS.map((p) => (
            <div key={p.key} className="flex items-center justify-between border rounded-md px-3 py-2">
              <Label htmlFor={`pref-${p.key}`} className="text-[12.5px] leading-relaxed flex-1 pr-4">
                {p.label}
              </Label>
              <Switch
                id={`pref-${p.key}`}
                checked={!!profile.notification_prefs?.[p.key]}
                onCheckedChange={(v) => updatePref(p.key, v)}
                data-testid={`profile-pref-${p.key}`}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={saving} variant="outline" data-testid="profile-save-notifications">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save preferences
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Login-email change with re-auth */}
      <Card data-testid="my-profile-email-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 opacity-70" /> Login email
          </CardTitle>
          <CardDescription>
            Changing your login email requires re-authentication. Existing sessions will be
            invalidated and you will be signed out for security.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Current login email" value={profile.email} disabled testid="profile-email-current" />
            <Field label="New login email" value={newEmail} onChange={setNewEmail}
                   placeholder="new.address@example.com" testid="profile-email-new" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Current password (required)
            </Label>
            <Input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Enter your current password"
              className="mt-1"
              data-testid="profile-email-current-password"
            />
          </div>
          {newEmail && newEmail !== profile.email && (
            <Alert>
              <AlertDescription className="text-[12px]">
                You will be signed out immediately after this change. Sign in again with your new email.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              onClick={saveEmail}
              disabled={changingEmail || !newEmail || newEmail === profile.email || !pwd}
              data-testid="profile-email-save"
            >
              {changingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Update login email
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator />
      <Alert data-testid="my-profile-security-note">
        <AlertDescription className="text-[12px]">
          <strong>What you cannot change here:</strong> your assigned role, permissions,
          division assignments, or another administrator&apos;s profile. Every save is captured
          in the audit log with the exact before and after values.
        </AlertDescription>
      </Alert>

      <Disclaimer />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, disabled, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}

function ReadOnly({ label, value, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="mt-1 px-3 py-2 text-[12.5px] rounded-md border bg-secondary/40 truncate"
           data-testid={testid} title={value || ""}>
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}
